import { Injectable, inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { UserAuthenticationService } from '../user-authentication/user-authentication.service';
import { ActivityStatusEnum } from 'src/app/models/activity-logs/activity-logs.model';
import type { ActivityLog, DataAnalysisActivity, TenantAnalyzeActivity } from 'src/app/models/activity-logs/activity-logs.model';
import { HubConnectionEndpoints, SignalRListener } from 'src/app/models/activity-logs/signalR.model';
import type { CheckedOutEntity, CheckedOutEntityType, ITenantInformationUpdate } from 'src/app/models/activity-logs/signalR.model';
import type { InstalledInfo, UserProduct, UserProductType } from 'src/app/models/user-products.model';
import { ActivityService } from '../activity/activity.service';
import { Store } from '@ngrx/store';
import type { AppState } from 'src/app/redux/app.state';
import { loadTokens, updateTokens } from 'src/app/redux/actions/tokens.actions';
import { loadTenantSharingSettings } from 'src/app/redux/actions/tenant-information.actions';
import { WorkflowRequestStatus } from 'src/app/models/workflow-request.model';
import type { IWorkflowRequest, WorkflowExecutionStatus } from 'src/app/models/workflow-request.model';
import { RunStatus } from 'src/app/models/automations/automations.model';
import type { IAutomation } from 'src/app/models/automations/automations.model';
import { ConnectionsService } from '../connections/connections.service';
import type { IConnection } from 'src/app/models/connection.model';
import { TemplateService } from '../templates/template.service';
import { WorkflowRequestService } from '../worflows-request/workflow-request.service';
import { WorkspaceService } from '../workspace/workspace.service';
import { AutomationsService } from '../automations/automations.service';
import { DashboardId } from 'src/app/modules/automate365-dashboard/enums/template.enum';
import { IntranetDataService } from 'src/app/modules/intranet-dashboard/services/intranet-data.service';
import { NotificationsService } from '../notifications/notifications.service';
import type { AnalyzerType, OriginType } from 'src/app/models/tenant-analzer/tenant-analzer.model';
import { TenantAnalyzerService } from '../tenant-analyzer/tenant-analyzer.service';
import { UserProductsService } from '../user-products/user-products.service';
import { ProductTypeEnum } from 'src/app/models/enums.model';
import { TemplateInstallationRequestsService } from '../template-installation-requests/template-installation-requests.service';
import type { TemplatePublishSettings } from 'src/app/models/template-creation-context.model';
import type { IPulseReportExport } from "src/app/modules/pulse365/modules/pulse365-dashboard/models/pulse365-report-export.model";
import { resetReportsRows } from 'src/app/modules/pulse365/modules/pulse365-dashboard/models/pulse365-report.model';
import { ConsentsPermissionsService } from 'src/app/modules/workspace/services/consents-permissions/consents-permissions.service';
import { UserTenantConsentApplicationEnum } from 'src/app/modules/workspace/models/consents-permissions.model';
import { CheckedOutEntitiesService } from '../checked-out-entities/checked-out-entities.service';
import { TenantHealthService } from 'src/app/modules/pulse365/modules/pulse365-dashboard/services/tenant-health/tenant-health.service';
import { PRODUCT_ID_BRANDCENTER, PRODUCT_ID_ORGNEWS, PRODUCT_ID_SECURESHARING, SettingsTemplateProvisioningService } from 'src/app/modules/automate365/settings-templates/settings-template-provisioning-module/settings-template-provisioning-service/settings-template-provisioning.service';
import { TenantMembershipInsightsType } from 'src/app/modules/pulse365/modules/pulse365-dashboard/models/pulse365-overview/tenant-membership-insights.model';
import type { TokenBalance } from '../licenses/licenses.model';
import { IntegrityScoreService } from 'src/app/integrity-score/services/integrity-score/integrity-score.service';
import type { SiteWithProductInstalled } from 'src/app/models/user-resources.model';
import { compareVersions, extractSiteShortName } from 'src/app/models/util.model';

@Injectable({
	providedIn: 'root',
})
export class SignalRService {
	private userAuthenticationService = inject(UserAuthenticationService);
	private activityService = inject(ActivityService);
	private connectionsService = inject(ConnectionsService);
	private templateService = inject(TemplateService);
	private workflowRequestService = inject(WorkflowRequestService);
	private workspaceService = inject(WorkspaceService);
	private automationsService = inject(AutomationsService);
	private intranetDashboardService = inject(IntranetDataService);
	private notificationsService = inject(NotificationsService);
	private tenantAnalyzerService = inject(TenantAnalyzerService);
	private userProductsService = inject(UserProductsService);
	private templateInstallationRequestsService = inject(TemplateInstallationRequestsService);
	private consentsService = inject(ConsentsPermissionsService);
	private settingsTemplateProvisioningService = inject(SettingsTemplateProvisioningService);
	private checkedOutEntitiesService = inject(CheckedOutEntitiesService);
	private tenantHealthService = inject(TenantHealthService);
	private integrityScoreService = inject(IntegrityScoreService);
	private store = inject<Store<AppState>>(Store);

	private adminAppHubConnection?: signalR.HubConnection;
	private publicPageHubConnection?: signalR.HubConnection;
	private selfServiceCatalogHubConnection?: signalR.HubConnection;

	private hubAPIUrl = environment.urls.hubApi;
	private readonly installedInfoProductTypes: UserProductType[] = ['theme', 'webpart', 'accessibility', 'sharedtheme'];

	// This is only for pulse reports for now can be utilized in other places
	private activeListeners: SignalRListener[] = [];

	// HUB CONNECTION BASE CONFIGURATION
	signalRConnectionBaseConfig(endpoint: HubConnectionEndpoints, queryString = "", accessTokenProvider?: () => Promise<string> | string) {
		return new signalR.HubConnectionBuilder()
			.withUrl(`${this.hubAPIUrl}${endpoint}${queryString}`, {
				accessTokenFactory: accessTokenProvider,
			})
			.withAutomaticReconnect() //This method configures the client to automatically attempt to reconnect in case the connection to the SignalR server is lost. It enables automatic reconnection functionality.
			.withStatefulReconnect() //This method configures stateful reconnect, meaning that the client will attempt to reconnect while maintaining its previous state.
			.configureLogging(signalR.LogLevel.None) //This line configures the logging level for SignalR. In this case, it sets the logging level to debug, which means more detailed information about the SignalR client's behavior will be logged.
			.build();
	}

	//#region ADMIN APP HUB CONNECTION
	startAdminAppConnection(workspaceId: string, tenantId: string): void {
		this.adminAppHubConnection = this.signalRConnectionBaseConfig(HubConnectionEndpoints.AdminApp, `?wid=${workspaceId}&tid=${tenantId}`, () => this.userAuthenticationService.getAuthenticatedUserAccessToken());
		this.adminAppHubConnection
			.start()
			.then(() => {
				this.activityService.getCurrentActivities()
				this.store.dispatch(loadTokens({ tenantId }));
				this.invoke_getCurrentCheckedOutEntities()
			})
			.catch((err) => console.error(err));
	}

	getAdminAppConnectionId(): string {
		return this.adminAppHubConnection?.connectionId ?? ""
	}

	addTokensUpdateListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.TokensUpdated, (balance?: TokenBalance) => {
			if (balance) {
				this.store.dispatch(updateTokens({ tokenBalance: balance }));
			}
		});
	}

	addIntegrityScoreListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.IntegrityScore, (overAllScore?: number) => {
			if (overAllScore !== undefined) {
				this.integrityScoreService.resetIntegrityScoreCache();
				this.integrityScoreService.updateOverAllScore(overAllScore);
				this.workspaceService.updateWorkspacesIntegrityScore(overAllScore);
			}
		})
	}

	addWorkspaceTemplateInstallationsListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.WorkspaceTemplateInstallations, async (activity?: ActivityLog[], getCurrentActivity?: boolean) => {
			if (activity && activity?.length > 0) {
				if (getCurrentActivity) {
					// get the new installations and update the store.
					// we only do this when a new activity is created.
					await this.templateInstallationRequestsService.getTemplateInstallationCurrentActivity();
				} else {
					this.templateInstallationRequestsService.redux_updateInstallationsActivity(activity);
				}
			}
		})
	}

	addWorkspaceTokensUpdatedListener(): void {
		this.adminAppHubConnection?.on('TokensUpdated', (balance?: TokenBalance) => {
			if (balance) {
				this.store.dispatch(updateTokens({ tokenBalance: balance }));
			}
		})
	}

	addPulse365ReportListener(callback: (pulseExport?: IPulseReportExport) => void): void {
		if (!this.activeListeners.includes(SignalRListener.Pulse365Report)) {
			this.adminAppHubConnection?.on(SignalRListener.Pulse365Report, callback)
			this.activeListeners.push(SignalRListener.Pulse365Report)
		}
	}

	addInstallListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.Install, async (activity?: ActivityLog[]) => {
			if (activity && activity?.length > 0) {
				if (activity[0].extrainformation?.runId) {
					this.automationsService.handleSignalRActivityLogs(activity)
					return;
				}

				// THIS IS FOR USERID/WORKSPACEID GROUP
				this.activityService.redux_addOrUpdateActivity(SignalRListener.Install, activity)

				const activitiesToSendNotification = activity.filter(x => [ActivityStatusEnum.Installed, ActivityStatusEnum.Error].includes(x.general.status))
				activitiesToSendNotification.forEach(activity => {
					const productName = activity.general.title
					switch (activity.general.status) {
						case ActivityStatusEnum.Installed:
							this.notificationsService.showSuccess(productName + " has been installed successfully", "Installation Complete", -1, undefined, ['view-details'], activity.general.taskId)
							void this.updateSitesWithProductInstalledFromActivity(activity);
							break;
						case ActivityStatusEnum.Error:
							this.notificationsService.showError("An error occurred during the installation of " + productName + ". Please check the installation report for more details.", "Installation Failed", -1, undefined, ['view-details'], activity.general.taskId)
							break;
					}

					// If an Organization News installation failed, clear local isOrgNews flag for that site
					try {
						const hasOrgNewsSubtask = activity.subTasks?.some(st => (st.productId ?? '').toLowerCase() === PRODUCT_ID_ORGNEWS);
						if (hasOrgNewsSubtask) {
							const siteUrl = activity.general.location ? decodeURIComponent(activity.general.location) : undefined;
							if (siteUrl) {
								// Update site based on activity status: mark installed when Installed, uninstalled when Error
								const installed = activity.general.status === ActivityStatusEnum.Installed;
								this.settingsTemplateProvisioningService.markInstalledSitesInStore(PRODUCT_ID_ORGNEWS, [{ url: siteUrl }], installed);
							}
						}
					} catch { /* non-fatal */ }

					// If a Brand Center installation refresh data and save on redux
					try {
						const hasBrandCenterSubtask = activity.subTasks?.some(st => (st.productId ?? '').toLowerCase() === PRODUCT_ID_BRANDCENTER);
						if (hasBrandCenterSubtask) {
							this.settingsTemplateProvisioningService.getBrandCenterStatusAndUpdateStore();
						}
					} catch { /* non-fatal */ }

					// If the Secure Sharing template was installed, refresh the tenant sharing settings in the store
					try {
						const hasSecureSharingSubtask = activity.subTasks?.some(st => { const pid = (st.productId ?? '').toLowerCase(); return pid === PRODUCT_ID_SECURESHARING || pid === 'sharing-settings'; });
						if (hasSecureSharingSubtask) {
							// Trigger the tenant-sharing reload via ngrx effect so the store is refreshed from the API
							this.store.dispatch(loadTenantSharingSettings({ application: UserTenantConsentApplicationEnum.Automate }));
						}
					} catch { /* non-fatal */ }
				})
			}
		})
	}

	addPulseActionListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.Pulse365Action, async (activity: ActivityLog[]) => {
			if (activity) {
				// THIS IS FOR USERID/WORKSPACEID GROUP
				this.activityService.redux_addOrUpdateActivity(SignalRListener.Pulse365Action, activity)

				const activitiesToSendNotification = activity.filter(x => [ActivityStatusEnum.Completed, ActivityStatusEnum.Skipped, ActivityStatusEnum.Error].includes(x.general.status))
				activitiesToSendNotification.forEach(async activity => {
					const productName = activity.general.title

					// common logic
					if (activity.general.status === ActivityStatusEnum.Completed || activity.general.status === ActivityStatusEnum.Error) {
						// Get orphaned count before resetting rows
						const oldOrphanedWorkspacesCount = this.tenantHealthService.getCurrentMembershipInsightsSnapshot()?.find(i => i.type === TenantMembershipInsightsType.OrphanedWorkspaces)?.total;

						// Refresh pulse table rows and top cards
						resetReportsRows();
						// Refresh the tenant health dashboard Pulse 365
						this.tenantAnalyzerService.refreshPulse365DashboardSubject.next();
						this.tenantAnalyzerService.refreshPulse365ReportSubject.next();

						// If orphaned count was loaded then we need to get the current count to check for differences
						if (oldOrphanedWorkspacesCount !== undefined) {
							const newOrphanedWorkspacesCount = (await this.tenantHealthService.getMembershipInsightsSnapshot(false).catch(() => null))?.find(i => i.type === TenantMembershipInsightsType.OrphanedWorkspaces)?.total;
							if (newOrphanedWorkspacesCount && newOrphanedWorkspacesCount > oldOrphanedWorkspacesCount) {
								this.notificationsService.showWarning(`The number of orphaned workspaces has increased from ${oldOrphanedWorkspacesCount} to ${newOrphanedWorkspacesCount}.`, "Orphaned Workspaces Alert", -1, undefined, ['open-orphaned-workspaces-report']);
							}
						}
					}

					// Show specific messages
					switch (activity.general.status) {
						case ActivityStatusEnum.Completed:
							this.notificationsService.showSuccess("Pulse Action: " + productName + " has been completed successfully", "Pulse Action Completed", -1, undefined, ['view-summary'], activity.general.taskId)
							break;
						case ActivityStatusEnum.Skipped:
							this.notificationsService.showWarning("Pulse Action: " + productName + " has been skipped. Please check the report for more details.", "Pulse Action Skipped", -1, undefined, ['view-summary'], activity.general.taskId)
							break;
						case ActivityStatusEnum.Error:
							this.notificationsService.showError("An error occurred during the completion of " + productName + " pulse action. Please check the report for more details.", "Pulse Action Failed", -1, undefined, ['view-summary'], activity.general.taskId)
							break;
					}
				})
			}
		})
	}

	addGenerateListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.Generate, (activity?: ActivityLog[], userProduct?: UserProduct, publishSettings?: TemplatePublishSettings) => {
			if (activity && activity?.length > 0) {
				this.activityService.redux_addOrUpdateActivity(SignalRListener.Generate, activity)
			}

			if (userProduct) {
				let prodType: UserProductType = 'theme';
				switch (userProduct.type) {
					case ProductTypeEnum.Theme:
						prodType = 'theme';
						break;
					case ProductTypeEnum.WebPart:
						prodType = 'webpart';
						break;
					case ProductTypeEnum.Accessibility:
						prodType = 'accessibility';
						break;
					case ProductTypeEnum.Template:
						prodType = 'template';
						break;
					default:
						return;
				}

				if (publishSettings && (publishSettings.microsoftCatalog || publishSettings.organizationCatalog)) {
					this.notificationsService.showSuccess("Product '" + userProduct.nameAlias + "' was sucessfully published.");
				}

				this.userProductsService.upsertUserProduct(prodType, userProduct);
				if (userProduct.status === "Generated") {
					this.notificationsService.showSuccess("Product '" + userProduct.nameAlias + "' is now available.");
				} else if (userProduct.status === "Error") {
					this.notificationsService.showWarning("Something wrong happened with the product '" + userProduct.nameAlias + "'. Please contact support.");
				}
			}
		})
	}

	addDownloadListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.Download, (activity?: ActivityLog[]) => {
			if (activity && activity?.length > 0) {
				this.activityService.redux_addOrUpdateActivity(SignalRListener.Download, activity)
			}
		})
	}

	addApplyListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.Apply, (activity?: ActivityLog[]) => {
			if (activity && activity?.length > 0) {
				this.activityService.redux_addOrUpdateActivity(SignalRListener.Apply, activity)

				const activitiesToSendNotification = activity.filter(x => [ActivityStatusEnum.Applied, ActivityStatusEnum.Error].includes(x.general.status))
				activitiesToSendNotification.forEach(activity => {
					const productName = activity.general.title
					switch (activity.general.status) {
						case ActivityStatusEnum.Applied:
							this.notificationsService.showSuccess(productName + " has been applied successfully", "Apply Complete", -1, undefined, ['view-details'], activity.general.taskId)
							void this.updateSitesWithProductInstalledFromActivity(activity);
							break;
						case ActivityStatusEnum.Error:
							this.notificationsService.showError("An error occurred during the apply of " + productName + ". Please check the apply report for more details.", "Apply Failed", -1, undefined, ['view-details'], activity.general.taskId)
							break;
					}
				})
			}
		})
	}

	addUpdateListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.Update, (activity?: ActivityLog[]) => {
			if (activity && activity?.length > 0) {
				this.activityService.redux_addOrUpdateActivity(SignalRListener.Update, activity)

				const activitiesToSendNotification = activity.filter(x => [ActivityStatusEnum.Updated, ActivityStatusEnum.Error].includes(x.general.status))
				activitiesToSendNotification.forEach(activity => {
					const productName = activity.general.title
					switch (activity.general.status) {
						case ActivityStatusEnum.Updated:
							this.notificationsService.showSuccess(productName + " has been updated successfully", "Update Complete", -1, undefined, ['view-details'], activity.general.taskId)
							void this.updateSitesWithProductInstalledFromActivity(activity);
							break;
						case ActivityStatusEnum.Error:
							this.notificationsService.showError("An error occurred during the update of " + productName + ". Please check the update report for more details.", "Update Failed", -1, undefined, ['view-details'], activity.general.taskId)
							break;
					}
				})
			}
		})
	}

	private async updateSitesWithProductInstalledFromActivity(activity: ActivityLog): Promise<void> {
		const location = this.normalizeActivityLocation(activity.general.location);
		if (!location) {
			return;
		}

		const productIds = [...new Set((activity.subTasks ?? [])
			.map(subTask => subTask.productId?.trim())
			.filter((productId): productId is string => !!productId)
			.map(productId => productId.toLowerCase()))];

		if (productIds.length === 0) {
			return;
		}

		const loadedProductsByType = await Promise.all(
			this.installedInfoProductTypes.map(async productType => ({
				productType,
				products: await this.userProductsService.checkUserProductIsLoadedByType(productType)
			}))
		);

		const productsIndex = new Map<string, { productType: UserProductType; product: UserProduct }>();
		for (const entry of loadedProductsByType) {
			if (!entry.products) {
				continue;
			}

			for (const product of entry.products) {
				if (!product?.id) {
					continue;
				}

				const key = product.id.toLowerCase();
				if (!productsIndex.has(key)) {
					productsIndex.set(key, { productType: entry.productType, product });
				}
			}
		}

		const indexedProducts: Array<{ productType: UserProductType; product: UserProduct; matchedProductId: string }> = productIds.flatMap(productId => {
			const indexedProduct = productsIndex.get(productId);
			if (!indexedProduct) {
				return [];
			}

			return [{ ...indexedProduct, matchedProductId: productId }];
		});

		if (indexedProducts.length === 0) {
			return;
		}

		for (const indexedProduct of indexedProducts) {
			const sitesWithProductInstalled = indexedProduct.product.sitesWithProductInstalled ? [...indexedProduct.product.sitesWithProductInstalled] : [];
			const locationKey = this.normalizeSiteUrlKey(location);
			const existingSiteIndex = sitesWithProductInstalled.findIndex(site => this.normalizeSiteUrlKey(site.url) === locationKey);
			const installedVersion = this.getInstalledVersionFromActivity(activity, indexedProduct.product, indexedProduct.matchedProductId);

			const siteToAdd: SiteWithProductInstalled = {
				url: location,
				installedVersion,
				siteName: extractSiteShortName(location)
			};

			if (existingSiteIndex >= 0) {
				sitesWithProductInstalled[existingSiteIndex] = siteToAdd;
			} else {
				sitesWithProductInstalled.push(siteToAdd);
			}

			this.userProductsService.updateUserProductSitesInstalledIn(indexedProduct.productType, indexedProduct.product.id, sitesWithProductInstalled);

			const latestKnownVersion = this.getLatestKnownVersion(indexedProduct.product);
			const hasOutdatedSite = sitesWithProductInstalled.some(site => compareVersions(site.installedVersion, latestKnownVersion) === -1);
			const shouldClearTenantOutdated = activity.general.status === ActivityStatusEnum.Updated || activity.general.status === ActivityStatusEnum.Applied;
			this.userProductsService.updateUserProductOutdatedInfo(indexedProduct.productType, indexedProduct.product.id, shouldClearTenantOutdated ? false : undefined, hasOutdatedSite);
		}
	}

	private getInstalledVersionFromActivity(activity: ActivityLog, product: UserProduct, matchedProductId?: string): string {
		const normalizedProductId = (matchedProductId ?? product.id)?.trim().toLowerCase();
		const relatedSubTask = (activity.subTasks ?? []).find(subTask => (subTask.productId ?? '').trim().toLowerCase() === normalizedProductId);

		const subTaskProductVersion = relatedSubTask?.productVersion?.trim();
		if (subTaskProductVersion) {
			return subTaskProductVersion;
		}

		if (activity.general.status === ActivityStatusEnum.Updated || activity.general.status === ActivityStatusEnum.Applied) {
			return this.getLatestKnownVersion(product);
		}

		if (activity.general.status === ActivityStatusEnum.Installed && product.version) {
			return product.version;
		}

		return this.getLatestKnownVersion(product);
	}

	private getLatestKnownVersion(product: UserProduct): string {
		const knownVersions = [
			product.version,
			...(product.versions ?? []).map(version => version.version)
		].filter((version): version is string => !!version);

		if(knownVersions.length === 0) {
			return '1.0.0.0';
		}
		return knownVersions.sort((a, b) => compareVersions(a, b)).at(-1) ?? knownVersions[0];
	}

	private normalizeSiteUrlKey(url?: string): string {
		if (!url) {
			return '';
		}

		return url.trim().toLowerCase().replace(/\/$/, '');
	}

	private normalizeActivityLocation(location?: string): string | undefined {
		if (!location) {
			return undefined;
		}

		try {
			return decodeURIComponent(location).trim();
		} catch {
			return location.trim();
		}
	}

	addInstalledInfoListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.InstalledInfo, (installedInfo?: InstalledInfo) => {
			if (installedInfo) {
				if (installedInfo.productsInstalledInfo) {
					this.userProductsService.loadUserProductsSiteOutdatedInfoSucess(installedInfo.productType, installedInfo.productsInstalledInfo)
				} else {
					this.userProductsService.loadUserProductsSiteInstalledInfoFailed(installedInfo.productType)
				}
			}
		})
	}

	addConnectionsListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.Connections, (connection?: IConnection) => {
			if (connection) {
				this.connectionsService.redux_updateConnection(connection)
			}
		});
	}

	addCheckedOutEntitiesListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.CheckedOutEntity, (entityType: CheckedOutEntityType, checkedOutEntity: CheckedOutEntity, action: "add" | "remove") => {
			if (checkedOutEntity) {
				if (action === "add") {
					this.checkedOutEntitiesService.redux_addCheckedOutEntity(entityType, checkedOutEntity)
				} else if (action === "remove") {
					this.checkedOutEntitiesService.redux_removeCheckedOutEntity(entityType, checkedOutEntity.id)
				}
			}
		})
	}

	addTenantAnalyzerListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.TenantAnalyzer, (tenantAnalyzeActivity?: TenantAnalyzeActivity, customMessage?: string, analyzerScanType?: AnalyzerType, origin?: OriginType, statisticsCompleted?: boolean, activity?: ActivityLog) => {
			if (tenantAnalyzeActivity) {
				this.tenantAnalyzerService.handleSignalRActivity(tenantAnalyzeActivity, customMessage, analyzerScanType, origin, statisticsCompleted);
			}
			if (activity && tenantAnalyzeActivity && tenantAnalyzeActivity.requestedBy === this.userAuthenticationService.getAuthenticatedUserId()) {
				this.activityService.redux_addOrUpdateActivity(SignalRListener.TenantAnalyzer, [activity])
			}
		});
	}

	addTenantInformationListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.TenantInfo, (tenantInformationUpdate?: ITenantInformationUpdate) => {
			if (tenantInformationUpdate) {
				const message = "<a class='btn-text underline text-16' href='/settings/workspace/tenant'>Review</a> updated list and implement any necessary changes in your SharePoint Admin Center.";
				if (tenantInformationUpdate.added.length > 0 || tenantInformationUpdate.deleted.length > 0) {
					this.notificationsService.showInfo(message, "Supported regions have been updated.", -1, undefined, []);
				}
			}
		});
	}

	addDataAnalysisListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.DataAnalysis, (dataAnalysisActivity?: DataAnalysisActivity) => {
			if (dataAnalysisActivity) {
				if (dataAnalysisActivity.dashboards.find(d => d === DashboardId.ProductsUsage)) {
					this.intranetDashboardService.updateReportProgress(dataAnalysisActivity.status);
				}
			}
		});
	}

	addAutomationsListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.AutomationsTrigger, (automation?: IAutomation) => {
			if (automation) {
				this.automationsService.redux_updateAutomation(automation)
			}
		});
	}

	addWorkflowsListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.WorkflowRequest, async (workflowRequests?: IWorkflowRequest[]) => {
			if (workflowRequests && workflowRequests.length > 0) {

				const userIsOwner = this.workspaceService.inCurrentWorkspaceIsOwner();
				const userIsAdmin = this.workspaceService.inCurrentWorkspaceIsAdmin();
				const userId = this.userAuthenticationService.getAuthenticatedUserId();
				const userTenant = await this.consentsService.getUserConsentByApplication(UserTenantConsentApplicationEnum.Automate);

				workflowRequests.forEach(async workflow => {
					// If is admin or owner of the workspace, the signal R will be updated or if the Requestor Id is the same of the workflow
					if (userIsOwner || userIsAdmin || (workflow.requester.id === userId || (userTenant && userTenant.identity?.email == workflow.requester.email))) {
						this.templateInstallationRequestsService.redux_upsertInstallationRequest(workflow)

						if (workflow.status !== WorkflowRequestStatus.Pending && (!workflow.userOrigin || !workflow.userOrigin.startsWith('DataDriven'))) {
							// Notify requester if workflow is not pending (completed or rejected)
							this.workflowRequestService.notifyRequester(workflow);
						}
						else {
							// Notify approver if the user is an approver
							this.workflowRequestService.notifyApprover(workflow);
						}
					}

					if (WorkflowRequestService.shouldUpdateMyWorkflows(workflow, userIsOwner, userId, userTenant?.identity?.email ?? "")) {
						this.workflowRequestService.upsertMyWorkflowRequest(workflow);
					}

					// Update the automation run status if the workflow is rejected
					if (workflow.status == WorkflowRequestStatus.Rejected) {
						const automationRuns = await this.automationsService.getLoadedAutomationsRuns();
						if (automationRuns) {
							const correspondingRun = automationRuns.find(run => run.installationId === workflow.taskId);
							if (correspondingRun) {
								correspondingRun.status = RunStatus.RejectedApproval;
								this.automationsService.redux_upsertAutomationRun(correspondingRun)
							}
						}
					}
				});
			}
		});
	}

	addWorkflowExecutionStatusListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.WorkflowExecutionStatus, (taskId: string, executionStatus: WorkflowExecutionStatus) => {
			this.workflowRequestService.redux_updateExecutionStatusMyWorkflow(taskId, executionStatus);
			this.templateInstallationRequestsService.redux_updateExecutionStatusWorkflow(taskId, executionStatus);
		});
	}

	addTemplatesUpdatesListener(): void {
		this.adminAppHubConnection?.on(SignalRListener.Templates, (requesterId: string, action: "update" | "delete" | "upsertdraft" | "publish" | "add" | "copy", param1: unknown, param2: unknown) => {
			if (param1) {
				if (action === "update") {
					this.templateService.redux_updateTemplate(param1 as UserProduct, param2 as TemplatePublishSettings)
				} else if (action === "upsertdraft") {
					// Specific scenario because draft require less validations and updates
					this.templateService.redux_upsertDraft(param1 as UserProduct)
				} else if (action === "delete") {
					this.templateService.redux_deleteTemplates(param1 as string[])
				} else if (action === "publish") {
					this.templateService.redux_updateTemplatePublishSettings(param1 as UserProduct, param2 as TemplatePublishSettings)
				} else if (action === "add") {
					// This is called in create template and duplicate template endpoints
					this.templateService.redux_addTemplate(param1 as UserProduct, param2 as TemplatePublishSettings)
				} else if (action === "copy") {
					for (const template of (param1 as UserProduct[])) {
						// Copy templates returns an array of templates and publish settings are always undefined
						this.templateService.redux_addTemplate(template, undefined)
					}
				}
			}
		})
	}

	//#region CHECK OUT ENTITIES METHODS
	invoke_getCurrentCheckedOutEntities(): Promise<CheckedOutEntity[]> {
		return new Promise<CheckedOutEntity[]>((resolve) => {
			this.adminAppHubConnection?.invoke("GetAllCheckedOutEntities")
				.then((resp) => {
					const checkedOutEntities = resp ?? []
					this.checkedOutEntitiesService.redux_setCheckedOutEntities(checkedOutEntities)
					resolve(checkedOutEntities)
				})
				.catch((err) => { console.log(err); resolve([]) })
		})
	}

	invoke_addCheckedOutEntity(entityType: CheckedOutEntityType, entityId: string): Promise<boolean> {
		return new Promise<boolean>((resolve) => {
			this.adminAppHubConnection?.invoke("AddCheckedOutEntity", entityType, entityId).then((resp) => resolve(resp)).catch(() => resolve(false))
		})
	}

	invoke_removeCheckedOutEntity(entityType: CheckedOutEntityType, entityId: string): Promise<boolean> {
		return new Promise<boolean>((resolve) => {
			this.adminAppHubConnection?.invoke("RemoveCheckedOutEntity", entityType).then((resp) => { this.checkedOutEntitiesService.redux_removeCheckedOutEntity(entityType, entityId); resolve(resp) }).catch(() => resolve(false))
		})
	}
	//#endregion CHECK OUT ENTITIES METHODS
	//#endregion ADMIN APP HUB CONNECTION

	//#region PUBLIC PAGE HUB CONNECTION
	startTaskIdConnection(taskId: string) {
		this.publicPageHubConnection = this.signalRConnectionBaseConfig(HubConnectionEndpoints.PublicPage, `?taskId=${taskId}`);
		this.publicPageHubConnection
			.start()
			.catch((err) => console.error(err));
	}

	addTaskIdWorkflowListener(): void {
		this.publicPageHubConnection?.on(SignalRListener.WorkflowRequest, (workflow: IWorkflowRequest) => {
			this.workflowRequestService.upsertMyWorkflowRequest(workflow);

			if (workflow.status !== WorkflowRequestStatus.Pending) {
				// Notify requester if workflow is not pending (completed or rejected)
				this.workflowRequestService.notifyRequester(workflow);
			}
		});
	}

	addTaskIdWorkflowExecutionStatusListener(taskId: string): void {
		this.publicPageHubConnection?.on(SignalRListener.WorkflowExecutionStatus, (executionStatus: WorkflowExecutionStatus) => {
			if (executionStatus) {
				this.workflowRequestService.redux_updateExecutionStatusMyWorkflow(taskId, executionStatus);
			}
		});
	}
	//#endregion PUBLIC PAGE HUB CONNECTION

	//#region SELF SERVICE CATALOG HUB CONNECTION
	startSelfServiceCatalogHubConnection(catalogId: string): void {
		this.selfServiceCatalogHubConnection = this.signalRConnectionBaseConfig(HubConnectionEndpoints.PublicPage, `?cid=${catalogId}`, () => this.userAuthenticationService.getPublicCatalogUserAuthenticatedUserAccessToken());
		this.selfServiceCatalogHubConnection
			.start()
			.then(() => {
				this.activityService.getCurrentActivities()
			})
			.catch((err) => console.error(err));
	}

	async stopSelfServiceCatalogHubConnection(): Promise<void> {
		await this.selfServiceCatalogHubConnection?.stop()
	}

	addSelfServiceCatalogInstallListener(): void {
		this.selfServiceCatalogHubConnection?.on(SignalRListener.Install, (activity?: ActivityLog[]) => {
			if (activity && activity.length > 0) {
				this.activityService.redux_addOrUpdateActivity(SignalRListener.Install, activity)
			}
		});
	}

	addSelfServiceCatalogWorkflowsListener(): void {
		this.selfServiceCatalogHubConnection?.on(SignalRListener.WorkflowRequest, (workflowRequests?: IWorkflowRequest[]) => {
			if (workflowRequests && workflowRequests.length > 0) {
				workflowRequests.forEach(workflow => {
					// If is admin or owner of the workspace, the signal R will be updated or if the Requestor Id is the same of the workflow
					if (workflow.requester.id === this.userAuthenticationService.getPublicCatalogUserAuthenticatedUserId()) {
						this.templateInstallationRequestsService.redux_upsertInstallationRequest(workflow);

						if (workflow.status !== WorkflowRequestStatus.Pending) {
							// Notify requester if workflow is not pending (completed or rejected)
							this.workflowRequestService.notifyRequester(workflow);
						}
						else {
							// Notify approver if the user is an approver
							this.workflowRequestService.notifyApprover(workflow);
						}
					}
				});
			}
		});
	}

	addSelfServiceCatalogWorkflowExecutionStatusListener(): void {
		this.selfServiceCatalogHubConnection?.on(SignalRListener.WorkflowExecutionStatus, (taskId: string, executionStatus: WorkflowExecutionStatus) => {
			this.workflowRequestService.redux_updateExecutionStatusMyWorkflow(taskId, executionStatus);
		});
	}
	//#endregion SELF SERVICE CATALOG HUB CONNECTION
}
